import { useState, useEffect, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Button,
} from "@mui/material";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import styles from "./pdf-viewer.module.scss";

// Simple worker configuration - use unpkg CDN which handles version matching automatically
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Region {
  page_number: number;
  polygon: number[];
  page_width: number;
  page_height: number;
  unit: string;
}

interface SourceLocation {
  page_number: number;
  polygon: number[]; // [x1, y1, x2, y2, x3, y3, x4, y4] - kept for backwards compatibility
  coordinate_system: string;
  page_width: number;
  page_height: number;
  unit: string;
  regions?: Region[]; // New: array of regions for multiple bounding boxes
}

interface PdfViewerWithHighlightsProps {
  documentUrl: string;
  sourceLocation?: SourceLocation;
  clauseNumber?: string;
  clauseIndex?: number;
  totalClauses?: number;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Error) => void;
}

export default function PdfViewerWithHighlights({
  documentUrl,
  sourceLocation,
  clauseNumber,
  clauseIndex = 0,
  totalClauses = 1,
  onLoadSuccess,
  onLoadError,
}: PdfViewerWithHighlightsProps): JSX.Element {
  const [numPages, setNumPages] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [currentRegionPageIndex, setCurrentRegionPageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // Determine available pages from regions
  const availablePages = useMemo(() => {
    if (sourceLocation?.regions && sourceLocation.regions.length > 0) {
      // Get unique page numbers from regions, sorted
      const pages = [...new Set(sourceLocation.regions.map(r => r.page_number))].sort((a, b) => a - b);
      return pages;
    }
    // Fallback to single page from source_location
    return [sourceLocation?.page_number || 1];
  }, [sourceLocation]);

  // Get current page to display based on region navigation
  const pageToShow = availablePages[currentRegionPageIndex] || availablePages[0] || 1;
  const hasMultiplePages = availablePages.length > 1;
  const scale = 1.0; // Fixed scale, no zoom

  // Reset region page index when clause changes
  useEffect(() => {
    setCurrentRegionPageIndex(0);
  }, [clauseIndex]);

  // Handle page changes (different page number)
  useEffect(() => {
    // Only reset pageLoaded if we're navigating to a different page
    // This forces the PDF page to reload
    setPageLoaded(false);
  }, [pageToShow]);

  // Handle clause navigation (including same page navigation)
  useEffect(() => {
    // Skip this effect on initial mount (clauseIndex = 0 initially)
    // Let the page load handler manage initial pageLoaded state
    if (clauseIndex === 0) return;

    // Clear canvas immediately when clause changes (forward or backward)
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx && canvasRef.current.width > 0) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }

    // For same-page navigation, ensure pageLoaded is true after canvas is ready
    const timer = setTimeout(() => {
      const pdfCanvas = containerRef.current?.querySelector(
        ".react-pdf__Page__canvas",
      ) as HTMLCanvasElement;

      // Only set pageLoaded if PDF canvas is actually ready
      if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
        setPageLoaded(true);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [clauseIndex]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
    onLoadSuccess?.();
  };

  const handleDocumentLoadError = (err: Error) => {
    console.error("Error loading PDF:", err);

    // Enhanced error messages based on error type
    let errorMessage = "Failed to load PDF document.";

    if (err.message.includes("worker")) {
      errorMessage =
        "PDF worker initialization failed. Please refresh the page and try again.";
    } else if (err.message.includes("fetch")) {
      errorMessage =
        "Unable to fetch the document. Please check your internet connection and document URL.";
    } else if (err.message.includes("password")) {
      errorMessage =
        "This document is password protected and cannot be displayed.";
    } else if (err.message.includes("Invalid PDF")) {
      errorMessage = "The document appears to be corrupted or invalid.";
    }

    setError(errorMessage);
    onLoadError?.(err);
  };

  const handlePageLoadSuccess = () => {
    // Wait for PDF canvas to be fully rendered before setting pageLoaded
    // This prevents race conditions on initial load
    setTimeout(() => {
      // Verify PDF canvas exists and has valid dimensions
      const pdfCanvas = containerRef.current?.querySelector(
        ".react-pdf__Page__canvas",
      ) as HTMLCanvasElement;

      if (pdfCanvas && pdfCanvas.width > 0 && pdfCanvas.height > 0) {
        setPageLoaded(true);
      } else {
        // Retry after a longer delay if canvas not ready
        setTimeout(() => setPageLoaded(true), 200);
      }
    }, 100);
  };

  // Draw bounding box when page loads
  useEffect(() => {
    if (!pageLoaded || !sourceLocation || !canvasRef.current || !containerRef.current) {
      return;
    }

    const drawBoundingBox = () => {
      const canvas = canvasRef.current;
      const pdfCanvas = containerRef.current?.querySelector(
        ".react-pdf__Page__canvas",
      ) as HTMLCanvasElement;
      const pageDiv = pageRef.current;

      if (!canvas || !pdfCanvas || !pageDiv) {
        console.warn("Canvas elements not ready for drawing");
        return false;
      }

      // Validate PDF canvas has valid dimensions before drawing
      const canvasWidth = pdfCanvas.width;
      const canvasHeight = pdfCanvas.height;

      if (canvasWidth === 0 || canvasHeight === 0) {
        console.warn("PDF canvas dimensions not ready:", { canvasWidth, canvasHeight });
        return false;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.warn("Canvas context not available");
        return false;
      }

      const displayWidth = pdfCanvas.offsetWidth;
      const displayHeight = pdfCanvas.offsetHeight;

      // Set canvas dimensions to match PDF canvas
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      // Clear previous drawings - essential for navigation
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Use regions array if available, otherwise fall back to legacy polygon
      const regionsToProcess = sourceLocation.regions && sourceLocation.regions.length > 0
        ? sourceLocation.regions.filter(region => region.page_number === pageToShow)
        : [{
            page_number: sourceLocation.page_number,
            polygon: sourceLocation.polygon,
            page_width: sourceLocation.page_width,
            page_height: sourceLocation.page_height,
            unit: sourceLocation.unit
          }];

      // Validate we have regions to process
      if (regionsToProcess.length === 0) {
        console.warn("No regions found for current page:", pageToShow);
        return false;
      }

      // Draw each region as a bounding box
      regionsToProcess.forEach((region, regionIndex) => {
        const { polygon, page_width, page_height } = region;

        // Validate region has all required data for drawing
        if (!polygon || polygon.length < 8) {
          console.warn("Invalid polygon data for region:", regionIndex, polygon);
          return;
        }

        if (!page_width || !page_height || page_width <= 0 || page_height <= 0) {
          console.warn("Invalid page dimensions for region:", regionIndex, { page_width, page_height });
          return;
        }

        // Calculate scale: canvas pixels per inch/unit
        const scaleX = canvasWidth / page_width;
        const scaleY = canvasHeight / page_height;

        // Transform polygon points from source units to pixels
        const points = [];
        for (let i = 0; i < polygon.length; i += 2) {
          const x = polygon[i] * scaleX;
          const y = polygon[i + 1] * scaleY;
          points.push({ x, y });
        }

        // Draw dashed bounding box with higher visibility
        ctx.strokeStyle = "#00a888";
        ctx.lineWidth = 3;
        ctx.setLineDash([10, 5]);
        ctx.fillStyle = "rgba(0, 168, 136, 0.12)";

        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Draw label badge for each region
        const labelX = Math.min(...points.map((p) => p.x));
        const labelY = Math.min(...points.map((p) => p.y)) - 30;
        const labelText = clauseNumber
          ? `SOURCE MATCH - CLAUSE ${clauseIndex + 1} (${clauseNumber})${regionsToProcess.length > 1 ? ` [${regionIndex + 1}/${regionsToProcess.length}]` : ''}`
          : `SOURCE MATCH - CLAUSE ${clauseIndex + 1}${regionsToProcess.length > 1 ? ` [${regionIndex + 1}/${regionsToProcess.length}]` : ''}`;

        ctx.setLineDash([8, 4]);
        ctx.fillStyle = "#e6f7f4";
        ctx.strokeStyle = "#00a888";
        ctx.lineWidth = 1.5;

        const padding = 8;
        ctx.font = "bold 11px Arial";
        const textMetrics = ctx.measureText(labelText);
        const badgeWidth = textMetrics.width + padding * 2;
        const badgeHeight = 20;
        const badgeX = labelX;
        const badgeY = Math.max(labelY, 10);

        // Draw rounded rectangle
        const radius = 3;
        ctx.beginPath();
        ctx.moveTo(badgeX + radius, badgeY);
        ctx.lineTo(badgeX + badgeWidth - radius, badgeY);
        ctx.quadraticCurveTo(
          badgeX + badgeWidth,
          badgeY,
          badgeX + badgeWidth,
          badgeY + radius,
        );
        ctx.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - radius);
        ctx.quadraticCurveTo(
          badgeX + badgeWidth,
          badgeY + badgeHeight,
          badgeX + badgeWidth - radius,
          badgeY + badgeHeight,
        );
        ctx.lineTo(badgeX + radius, badgeY + badgeHeight);
        ctx.quadraticCurveTo(
          badgeX,
          badgeY + badgeHeight,
          badgeX,
          badgeY + badgeHeight - radius,
        );
        ctx.lineTo(badgeX, badgeY + radius);
        ctx.quadraticCurveTo(badgeX, badgeY, badgeX + radius, badgeY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Badge text
        ctx.fillStyle = "#00a888";
        ctx.fillText(labelText, badgeX + padding, badgeY + badgeHeight / 2 + 4);
      });

      ctx.setLineDash([]);

      return true; // Successfully drew bounding box
    };

    // Retry mechanism for robust initial load
    let retryCount = 0;
    const maxRetries = 3;

    const attemptDraw = () => {
      const success = drawBoundingBox();

      if (!success && retryCount < maxRetries) {
        retryCount++;
        console.log(`Retrying bounding box draw (attempt ${retryCount}/${maxRetries})`);
        setTimeout(attemptDraw, 200 * retryCount); // Exponential backoff
      }
    };

    // Draw with multiple animation frames to ensure PDF is fully rendered
    // Increased timeout for initial load reliability
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(attemptDraw); // Extra frame + retry logic
          });
        });
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [
    pageLoaded,
    clauseIndex,
    clauseNumber,
    sourceLocation?.page_number,
    sourceLocation?.page_width,
    sourceLocation?.page_height,
    JSON.stringify(sourceLocation?.polygon), // Serialize array for proper change detection
    JSON.stringify(sourceLocation?.regions), // Serialize regions array for proper change detection
  ]);

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Alert
          severity="error"
          sx={{ maxWidth: 500 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              Retry
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!documentUrl) {
    return (
      <Box className={styles.emptyContainer}>
        <Typography variant="body2" color="text.secondary">
          No document URL provided
        </Typography>
      </Box>
    );
  }

  const handlePreviousPage = () => {
    if (currentRegionPageIndex > 0) {
      setCurrentRegionPageIndex(currentRegionPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (currentRegionPageIndex < availablePages.length - 1) {
      setCurrentRegionPageIndex(currentRegionPageIndex + 1);
    }
  };

  return (
    <Box className={styles.pdfViewerContainer}>
      {sourceLocation && numPages > 0 && (
        <Box sx={{
          mb: 2,
          px: 2,
          py: 1,
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2
        }}>
          <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
            <strong>Focused View:</strong> Showing page {pageToShow} of{" "}
            {numPages}
            {numPages > 1 && ` • Document contains ${numPages} pages total`}
            {hasMultiplePages && (
              <span style={{ color: "#00a888", marginLeft: "8px" }}>
                • Clause spans {availablePages.length} pages
              </span>
            )}
          </Typography>
          {hasMultiplePages && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
                Region Page {currentRegionPageIndex + 1} of {availablePages.length}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={currentRegionPageIndex === 0}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  fontSize: "11px",
                  textTransform: "none",
                  borderColor: "#00a888",
                  color: "#00a888",
                  "&:hover": {
                    borderColor: "#00947a",
                    backgroundColor: "rgba(0, 168, 136, 0.08)",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                ‹ Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleNextPage}
                disabled={currentRegionPageIndex >= availablePages.length - 1}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  fontSize: "11px",
                  textTransform: "none",
                  borderColor: "#00a888",
                  color: "#00a888",
                  "&:hover": {
                    borderColor: "#00947a",
                    backgroundColor: "rgba(0, 168, 136, 0.08)",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                Next ›
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Box className={styles.documentContainer} ref={containerRef}>
        <Document
          file={documentUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          loading={
            <Box className={styles.loadingContainer}>
              <CircularProgress size={40} sx={{ color: "#00a888" }} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading document...
              </Typography>
            </Box>
          }
        >
          {/* Only render the page with the bounding box */}
          <Box
            ref={pageRef}
            style={{ position: "relative", display: "inline-block" }}
          >
            <Page
              pageNumber={pageToShow}
              scale={scale}
              onLoadSuccess={handlePageLoadSuccess}
              loading={
                <Box className={styles.loadingContainer}>
                  <CircularProgress size={30} sx={{ color: "#00a888" }} />
                </Box>
              }
            />
            {/* Canvas overlay for bounding box */}
            <canvas
              ref={canvasRef}
              className={styles.highlightCanvas}
              style={{
                position: "absolute",
                top: 0,
                // left: 0,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Document>
      </Box>
    </Box>
  );
}
